import React from 'react';
import { Box, Container, Grid, Typography, Paper, Link as MuiLink } from '@mui/material';
import { useParams, Link, useNavigate } from 'react-router-dom';
import BlogSidebar from '../components/BlogSidebar';
import { blogPosts } from '../data/blogData';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PersonIcon from '@mui/icons-material/Person';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';

const BlogPostScreen = () => {
    const { slug } = useParams();
    const navigate = useNavigate();
    const postIndex = blogPosts.findIndex(p => p.slug === slug);
    const post = blogPosts[postIndex];

    const prevPost = postIndex > 0 ? blogPosts[postIndex - 1] : null;
    const nextPost = postIndex < blogPosts.length - 1 ? blogPosts[postIndex + 1] : null;

    if (!post) {
        return (
            <Container sx={{ py: 10, textAlign: 'center' }}>
                <Typography variant="h4">Post not found</Typography>
                <Link to="/news-and-updates" style={{ color: '#007bff', marginTop: '20px', display: 'inline-block' }}>Go back to News and Updates</Link>
            </Container>
        );
    }

    return (
        <Box sx={{ minHeight: '100vh', fontFamily: '"Lato", "Open Sans", sans-serif' }}>
            {/* Header Section */}
            <Box sx={{ bgcolor: '#87CEEB', py: 4, mb: 5 }}> {/* Light blue background */}
                <Container maxWidth="lg">
                    <Grid container justifyContent="space-between" alignItems="center">
                        <Grid item>
                            <Typography variant="h3" sx={{ color: '#fff', fontWeight: '400', fontSize: '30px' }}>
                                My Blog
                            </Typography>
                        </Grid>
                        <Grid item>
                            <Box sx={{ display: 'flex', alignItems: 'center', color: '#fff', fontSize: '13px' }}>
                                <MuiLink component={Link} to="/" sx={{ color: '#fff', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>home</MuiLink>
                                <Typography sx={{ mx: 0.5 }}> / </Typography>
                                <MuiLink component={Link} to="/news-and-updates" sx={{ color: '#fff', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>blog</MuiLink>
                                <Typography sx={{ mx: 0.5 }}> / </Typography>
                                <Typography sx={{ color: '#fff' }}>uncategorized</Typography>
                                <Typography sx={{ mx: 0.5 }}> / </Typography>
                                <Typography sx={{ color: '#fff', maxWidth: '300px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{post.title.toLowerCase()}</Typography>
                            </Box>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            <Container maxWidth="lg">
                <Grid container spacing={4}>
                    {/* Main Content */}
                    <Grid item xs={12} md={8}>
                        <Paper elevation={0} sx={{ p: 0, mb: 4, borderRadius: 0 }}>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 3 }}>
                                <Box sx={{ bgcolor: '#87CEEB', p: 1, mr: 2, borderRadius: 1 }}> {/* Icon placeholder style */}
                                    <FolderOpenIcon sx={{ color: '#fff', fontSize: '24px' }} />
                                </Box>
                                <Box>
                                    <Typography variant="h4" sx={{ color: '#333', fontWeight: 'bold', mb: 1, fontSize: '24px', lineHeight: 1.2 }}>
                                        {post.title}
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2, color: '#999', fontSize: '12px', fontStyle: 'italic' }}>
                                        <Typography component="span" sx={{ fontSize: '12px' }}>by {post.author}</Typography>
                                        <Typography component="span" sx={{ fontSize: '12px' }}>on {post.date}</Typography>
                                        <Typography component="span" sx={{ fontSize: '12px' }}>Comments Off</Typography>
                                    </Box>
                                </Box>
                            </Box>

                            {post.imageUrl && (
                                <Box sx={{ mb: 3 }}>
                                    <img src={post.imageUrl} alt={post.title} style={{ width: '100%', height: 'auto', display: 'block' }}
                                        onError={(e) => { e.target.style.display = 'none'; }} />
                                </Box>
                            )}

                            <Box sx={{
                                color: '#666',
                                lineHeight: 1.8,
                                fontSize: '14px',
                                textAlign: 'start',
                                '& p': { mb: 2 },
                                '& a': { color: '#007bff', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }
                            }} dangerouslySetInnerHTML={{ __html: post.content }} />

                            <Box sx={{ mt: 4, mb: 4, display: 'flex', alignItems: 'center' }}>
                                <FolderOpenIcon sx={{ fontSize: '16px', mr: 0.5, color: '#888' }} />
                                <Typography sx={{ fontSize: '13px', color: '#888' }}>
                                    {post.category}
                                </Typography>
                            </Box>

                            {/* Navigation */}
                            <Grid container justifyContent="space-between" alignItems="center" sx={{ mt: 4 }}>
                                <Grid item>
                                    {prevPost && (
                                        <Box
                                            component={Link}
                                            to={`/news-and-updates/${prevPost.slug}`}
                                            sx={{
                                                display: 'inline-block',
                                                bgcolor: '#87CEEB',
                                                color: '#fff',
                                                px: 3,
                                                py: 1,
                                                textDecoration: 'none',
                                                fontSize: '13px',
                                                borderRadius: '2px', // Slight rounding if needed, or 0
                                                transition: 'background-color 0.3s',
                                                '&:hover': { bgcolor: '#5bc0de' }
                                            }}
                                        >
                                            &lt; Previous
                                        </Box>
                                    )}
                                </Grid>
                                <Grid item>
                                    {nextPost && (
                                        <Box
                                            component={Link}
                                            to={`/news-and-updates/${nextPost.slug}`}
                                            sx={{
                                                display: 'inline-block',
                                                bgcolor: '#87CEEB',
                                                color: '#fff',
                                                px: 3,
                                                py: 1,
                                                textDecoration: 'none',
                                                fontSize: '13px',
                                                borderRadius: '2px',
                                                transition: 'background-color 0.3s',
                                                '&:hover': { bgcolor: '#5bc0de' }
                                            }}
                                        >
                                            Next &gt;
                                        </Box>
                                    )}
                                </Grid>
                            </Grid>
                        </Paper>
                    </Grid>

                    {/* Sidebar */}
                    <Grid item xs={12} md={4}>
                        <BlogSidebar />
                    </Grid>
                </Grid>
            </Container>
        </Box>
    );
};

export default BlogPostScreen;
